import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { getDisplayValueFromList, priorityOptionsList } from 'components/utils';
import { useTranslation } from 'react-i18next';
import calendarGreyIcon from 'assets/images/icons/calendar-grey.svg';
import dataSourceIcon from 'assets/images/icons/data-source.svg';
import userGreyIcon from 'assets/images/icons/user-grey.svg';
import formGreyIcon from 'assets/images/icons/form-grey.svg';
import labelIcon from 'assets/images/icons/label.svg';
import linkExternalIcon from 'assets/images/icons/link-external.svg';
import { getAssignmentDisplayValue } from '../utils';
import Badge from 'components/common-components/Badge';

const TaskDetailForm = ({ data, allUsers, allGroups }) => {
  const { t } = useTranslation();

  const [values] = useState({
    referenceNumber: data.referenceNumber || '',
    title: data.title || '',
    description: data.description || '',
    formTitle: data.formTitle || '',
    status: data.status || '',
    priority: data.priority || '',
    companyField: data.companyField?.fieldTitle || '',
    endDate: data.endDate || '',
    labels: data.labels || [],
    assignedUsers: data.assignedUsers || [],
    assignedGroups: data.assignedGroups || [],
    location: data.location || '',
    createdBy: data.createdBy ? `${data.createdBy.firstName} ${data.createdBy.lastName}` : '',
    createdAt: data.createdAt || '',
  });

  function formatIsoToEuropeanDateTime(isoDate) {
    if (!isoDate) return '';
    const date = new Date(isoDate);

    const options = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    };

    const parts = new Intl.DateTimeFormat('en-GB', options).formatToParts(date);

    const get = (type) => parts.find((p) => p.type === type)?.value;

    return `${get('day')}/${get('month')}/${get('year')} ${get('hour')}:${get('minute')} ${get(
      'dayPeriod',
    )}`;
  }

  function formatIsoToUSDateTime(isoString) {
    const date = new Date(isoString);

    const parts = new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }).formatToParts(date);

    const get = (type) => parts.find((p) => p.type === type)?.value;

    return `${get('month')} ${get('day')}, ${get('year')} ${get('hour')}:${get('minute')} ${get(
      'dayPeriod',
    )}`;
  }

  const displayTaskStatus = (data) => {
    return (
      <div>
        {data === 'todo' ? (
          <Badge text='text_todo' variant='orange-2' />
        ) : data === 'in_progress' ? (
          <Badge text='text_in_progress' variant='blue-2' />
        ) : data === 'completed' ? (
          <Badge text='text_completed' variant='green-2' />
        ) : data === 'closed' ? (
          <Badge text='text_closed' variant='grey-2' />
        ) : (
          <></>
        )}
      </div>
    );
  };

  return (
    <div className='h-100 p-3 theme-background-white-5'>
      <div className='d-flex flex-column gap-3 px-4 py-3 theme-background-white-1 rounded-md'>
        <span className='px-2 py-1 theme-background-white-3 rounded-md align-self-start'>
          {`TK-${values.referenceNumber}`}
        </span>
        <span className='theme-font-inter-bold theme-size-1_3'>{values.title}</span>
        {values.description && (
          <span className='theme-font-inter-regular'>{values.description}</span>
        )}

        <div className='w-100 d-flex flex-column gap-3'>
          <div className='row align-items-center'>
            <div className='col-4 my-auto'>
              <span className=''>{t('text_status')}</span>
            </div>
            <div className='col-8 theme-font-inter-semi-bold'>
              {displayTaskStatus(values.status)}
            </div>
          </div>

          <div className='row align-items-center'>
            <div className='col-4'>
              <span className=''>{t('text_company_field')}</span>
            </div>
            <div className='col-8'>
              {values.companyField && (
                <div className='d-flex gap-2 align-items-center'>
                  <img src={dataSourceIcon} />
                  <span className='theme-font-inter-semi-bold'>{values.companyField}</span>
                </div>
              )}
            </div>
          </div>

          <div className='row align-items-center'>
            <div className='col-4'>{t('text_priority')}</div>
            <div className='col-8'>
              <div className='d-flex gap-2 align-items-center'>
                <img src={priorityOptionsList.find((o) => o.value === values.priority)?.icon} />
                <span className='theme-font-inter-semi-bold'>
                  {t(
                    getDisplayValueFromList(priorityOptionsList, values.priority, 'select_select'),
                  )}
                </span>
              </div>
            </div>
          </div>

          <div className='row align-items-center'>
            <div className='col-4'>{t('text_due_date')}</div>
            <div className='col-8'>
              {values.endDate && (
                <div className='d-flex gap-2 align-items-center'>
                  <img src={calendarGreyIcon} />
                  <span className='theme-font-inter-semi-bold text-uppercase'>
                    {formatIsoToEuropeanDateTime(values.endDate)}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className='row align-items-center'>
            <div className='col-4'>
              <span className='d-block mt-2'>{t('text_label')}</span>
            </div>
            <div className='col-8'>
              {values.labels.length > 0 && (
                <div className='d-flex gap-2 align-items-start'>
                  <img className='mt-2' src={labelIcon} />
                  <div className='d-flex gap-2 flex-wrap'>
                    {values.labels.map((label, index) => (
                      <div
                        key={index}
                        style={{ backgroundColor: label.color }}
                        className='theme-text-white-1 py-1 px-2 rounded-sm text-nowrap'
                      >
                        {label.name}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className='row align-items-center'>
            <div className='col-4'>{t('text_assigned_to')}</div>
            <div className='col-8'>
              {(values.assignedUsers.length > 0 || values.assignedGroups.length > 0) && (
                <div className='d-flex gap-2 align-items-center'>
                  <img className='cursor pointer' src={userGreyIcon} />
                  <span>
                    {getAssignmentDisplayValue(
                      allUsers.filter((u) => values.assignedUsers.includes(u._id)),
                      allGroups.filter((g) => values.assignedGroups.includes(g._id)),
                    )}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className='row align-items-center'>
            <div className='col-4'>{t('text_location')}</div>
            <div className='col-8'>
              {values.location && (
                <div className='d-flex gap-3 align-items-center'>
                  <span>{values.location}</span>
                  <img
                    className='cursor-pointer'
                    src={linkExternalIcon}
                    onClick={() =>
                      window.open(`https://www.google.com/maps?q=${values.location}`, '_blank')
                    }
                  />
                </div>
              )}
            </div>
          </div>

          <span className='text-center my-3'>
            {`Created by ${values.createdBy} At 
            ${formatIsoToUSDateTime(values.createdAt)}`}
          </span>
        </div>
      </div>
      <div className='d-flex flex-column gap-3 mt-3 px-4 py-3 theme-background-white-1 rounded-md'>
        <div className='d-flex gap-3 align-items-center'>
          <img className='cursor pointer' src={formGreyIcon} />
          <span className=''>{t('text_form')}</span>
        </div>
        <div className='theme-font-inter-semi-bold theme-size-1_1'>{values.formTitle}</div>
      </div>
    </div>
  );
};

TaskDetailForm.propTypes = {
  data: PropTypes.object.isRequired,
  allUsers: PropTypes.array.isRequired,
  allGroups: PropTypes.array.isRequired,
};

export default TaskDetailForm;
